import { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import heic2any from 'heic2any'
import moment from 'moment'
import styled from '@emotion/styled'
import {
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  Box,
  Divider
} from '@mui/material'
import { MdOutlineFileUpload } from 'react-icons/md'
import ShareFileLogo from 'assets/images/share-file-logo.png'
import DragAndDropCard from './DragAndDropCard'
import {
  setAllCustomerFiles,
  setUpdatedDoc,
  setSingleDoc,
  setFilterFiles,
  setAllFilesLoader,
  selectCustomerFilesList
} from 'slices/customerFiles'
import { setIdleTime } from 'slices/global'
import { useProcessAllFiles } from 'api'
import { generateUUID, collectDroppedFileNames } from 'utils/helpers'
import { useCustomer } from 'api/useCustomer'
import { api } from 'lib/axios'
import { selectCustomer, updateCustomerField } from 'slices/customer'
import { useDocumentsClassificationProcess } from 'api'
import {
  useDocumentStatusChange,
  useOcrQueueProcessor,
  useBatchOCRMultiThread
} from 'api'
import buildOcrQueueJobPayload from 'utils/buildOcrQueueJobPayload'
import { selectTripAir } from 'slices/tripAirSlice'
import { selectTripSea } from 'slices/tripseaSlice'
import { selectPassport } from 'slices/passport'
import { selectAuth } from 'slices/auth'
import { USER_ROLES } from 'config/roles'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'lib'
import {
  setLoading,
  setLoadingMessage,
  selectIsLoading,
  selectLoadingMessage
} from 'slices/loaderSlice'
import { setTravelModal, selectIsCategorizing } from 'slices/global'
import { setCurrentTab } from 'slices/customerSteps'
import { getUserId } from 'lib/auth/tokenStorage'
import { useLogActivity } from 'api'
import { incrementImageRefreshCounter } from 'slices/customer'
import { useAutoStatusChange } from 'hooks/useAutoStatusChange'
import { buildCardCheckOverrides } from 'utils/ocrPayload'

const prHasDocuments = pr =>
  Boolean(
    pr?.files?.pos_receipt?.length > 0 ||
    pr?.files?.sat_invoice?.length > 0 ||
    pr?.files?.purchase_stamp?.some(group => group?.length > 0) ||
    pr?.purchase_others?.some(group => group?.length > 0) ||
    pr?.payment_methods?.some(pm => pm?.files?.slip?.length > 0)
  )

const UploadDocuments = ({
  customer,
  passport,
  DragDropTimeLog,
  onRetryUploadReady,
  setAlertModal
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const fileInputRef = useRef(null)
  const processingStatus = useSelector(
    state => state.customer.processing_statuses_c
  )
  const customer_files = useSelector(state => state.customerFiles)
  const currentCustomerFiles = useSelector(state => state.customer_files?.file || state.customer_files?.docs)
  const customerInfo = useSelector(selectCustomer)
  const customerFiles = useSelector(selectCustomerFilesList)
  const tripAir = useSelector(selectTripAir)
  const tripSea = useSelector(selectTripSea)
  const passportData = useSelector(selectPassport)
  const purchase_cards = useSelector(state => state?.paymentCards?.paymentCards)
  const purchase_requests = useSelector(state => state?.purchaseReceipt?.pr)
  const { handleProcessAllFiles } = useProcessAllFiles()
  const auth = useSelector(selectAuth)
  const userRole = auth?.data?.user?.affiliate_role || ''
  const isAgent = userRole.toLowerCase() === USER_ROLES.AGENT
  const isDocsUploadedYes = customerInfo?.docs_uploaded_c === 'yes'
  const shouldDisableToggleForAgent = isAgent && isDocsUploadedYes
  const { logBatchApiActivity, logApiActivity } = useLogActivity()
  const { checkAndUpdateStatus } = useAutoStatusChange()
  const formattedDate = moment().format('DD/MM/YYYY')
  const { handleDocumentsClassificationProcess } =
    useDocumentsClassificationProcess()
  const { handleDocumentStatusChange } = useDocumentStatusChange()
  const { handleOcrQueueProcessor } = useOcrQueueProcessor()
  const { handleBatchOCRMultiThread, isBatchOCRMultiThreadDataLoading } =
    useBatchOCRMultiThread()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isLoading = useSelector(selectIsLoading)
  const loadingMessage = useSelector(selectLoadingMessage)
  const isCategorizing = useSelector(selectIsCategorizing)
  const getInitialToggleValue = () => {
    if (customerInfo?.docs_uploaded_c === 'yes') {
      return 'documents_uploaded'
    }
    return 'documents_not_uploaded'
  }
  const getInitialManualClassToggleValue = () => {
    if (customerInfo?.run_manual_ocr_c === 'yes') {
      return 'yes'
    }
    return 'no'
  }
  const [toggleValue, setToggleValue] = useState(getInitialToggleValue)
  const [manualClassToggleValue, setManualClassToggleValue] = useState(
    getInitialManualClassToggleValue
  )
  useEffect(() => {
    const newToggleValue = getInitialToggleValue()
    setToggleValue(newToggleValue)
  }, [customerInfo?.docs_uploaded_c])
  useEffect(() => {
    const newManualClassToggleValue = getInitialManualClassToggleValue()
    setManualClassToggleValue(newManualClassToggleValue)
  }, [customerInfo?.run_manual_ocr_c])
  const [preparingFiles, setPreparingFiles] = useState(false)
  const [, setDuplicateImagesFiles] = useState([])
  const [, setUploadFailed] = useState(false)
  const [, setUploadFailedText] = useState('')
  const [, setData] = useState({
    loading: false,
    error: '',
    message: ''
  })
  const { handleSyncDataDocs } = useCustomer()
  const [localFiles, setLocalFiles] = useState(new Map())
  const isAnyDocumentUploading = useMemo(() => {
    return (
      currentCustomerFiles?.some(file => {
        const isUploaded = file.isUploaded
        const progress = file.progress || 0
        return !isUploaded && progress < 100
      }) || preparingFiles
    )
  }, [currentCustomerFiles, preparingFiles])

  // Manual Classification toggle state and enablement
  const [isManualClassWorking, setIsManualClassWorking] = useState(false)
  const normalizedProcessingStatus = String(
    processingStatus || ''
  ).toLowerCase()
  const isManualClassAllowed =
    normalizedProcessingStatus === 'documents_classification_failed' ||
    normalizedProcessingStatus === 'classification failed'

  // Documents Uploaded toggle state and enablement
  const [isDocumentsUploadedWorking, setIsDocumentsUploadedWorking] =
    useState(false)

  const logActivity = async (action, customerId) => {
    try {
      const agentId = localStorage.getItem('agent_id')
      if (!agentId || !customerId) return

      const now = moment()
      const utcMoment = now.utc()
      const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

      const body = {
        data: {
          type: 'gb_action_tracking_logs',
          attributes: {
            affiliate_member_id: agentId,
            action: action,
            action_time: formattedTime,
            parent_id: customerId
          }
        }
      }
      await api.post('/Api/V8/module', body)
    } catch (error) {
      console.error('Error logging activity:', error)
    }
  }

  const handleManualClassificationToggle = async newValue => {
    if (!newValue) return
    if (newValue !== 'yes') {
      setManualClassToggleValue(newValue)
      return
    }
    if (!customerInfo?.id) return
    setManualClassToggleValue(newValue)
    dispatch(
      updateCustomerField({
        field: 'processing_statuses_c',
        value: 'classification_manually_completed'
      })
    )
    const agentId = localStorage.getItem('agent_id')
    setIsManualClassWorking(true)
    try {
      await handleDocumentStatusChange({
        contact_id: customerInfo.id,
        agent_id: agentId,
        processing_statuses_c: 'classification_manually_completed'
      })
      const payload = {
        ...buildOcrQueueJobPayload({
          tripAir,
          tripSea,
          passport: passportData,
          purchase_cards,
          purchase_requests,
          customer: customerInfo
        }),
        run_manual_ocr_c: newValue
      }
      if (Array.isArray(payload?.documents) && payload.documents.length > 0) {
        await handleOcrQueueProcessor(payload)
      } else {
        console.error('No documents found for OCR processing')
      }
      await logActivity('manual_classification_complete', customerInfo.id)
    } catch (err) {
      console.error('Manual classification flow failed:', err)
    } finally {
      setIsManualClassWorking(false)
    }
  }
  const handleDocumentsUploadedStatusChange = async newValue => {
    if (!newValue) return
    if (newValue !== 'documents_uploaded') {
      setToggleValue(newValue)
      const docsUploadedValue = newValue === 'documents_uploaded' ? 'yes' : 'no'
      dispatch(
        updateCustomerField({
          field: 'docs_uploaded_c',
          value: docsUploadedValue
        })
      )
      return
    }
    if (newValue === 'documents_uploaded' && isAnyDocumentUploading) {
      return
    }
    if (!customerInfo?.id) return
    setToggleValue(newValue)
    const docsUploadedValue = newValue === 'documents_uploaded' ? 'yes' : 'no'
    dispatch(
      updateCustomerField({
        field: 'docs_uploaded_c',
        value: docsUploadedValue
      })
    )
    dispatch(
      updateCustomerField({
        field: 'processing_statuses_c',
        value: 'documents_uploaded'
      })
    )
    const agentId = localStorage.getItem('agent_id')
    setIsDocumentsUploadedWorking(true)
    try {
      await handleDocumentStatusChange({
        contact_id: customerInfo.id,
        agent_id: agentId,
        processing_statuses_c: 'documents_uploaded'
      })
      const documents = Array.isArray(customerFiles)
        ? customerFiles
          .map(file => {
            const documentRevisionId = file?.file_obj?.document_revision_id
            const filename =
              file?.file_obj?.file_name ||
              file?.fileName ||
              file?.file_obj?.name
            if (!documentRevisionId || !filename) return null
            return {
              document_revision_id: documentRevisionId,
              filename
            }
          })
          .filter(Boolean)
        : []

      if (documents.length > 0) {
        const payload = {
          contact_id: customerInfo.id,
          documents
        }
        await handleDocumentsClassificationProcess(payload)
      } else {
        console.error('No documents found for classification processing')
      }
      await logActivity('documents_uploaded', customerInfo.id)
    } catch (err) {
      console.error('Error classifying documents:', err)
    } finally {
      setIsDocumentsUploadedWorking(false)
    }
  }
  const uploadStartTimeRef = useRef(null)
  const uploadEndTimeRef = useRef(null)
  const uploadCountRef = useRef(0)
  const completedUploadsRef = useRef(0)
  const retryingIdsRef = useRef(new Set())

  const prepareData = (array = [], docs = []) => {
    const arr = array.filter(item => {
      const { file_obj } = item
      const newArr = !docs.some(
        doc =>
          doc?.file_obj?.document_revision_id === file_obj?.document_revision_id
      )
      return newArr
    })
    return arr.map((item, idx) => {
      const { file_obj } = item

      return {
        file_no: idx,
        file: {},
        id: generateUUID(),
        fileName: file_obj?.name || '',
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: true,
        isError: false,
        file_obj: file_obj || {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: null
        }
      }
    })
  }

  const filterAllDocs = (files, docs) => {
    const docIds = new Set(docs.map(doc => doc.file_obj.id))
    return files.filter(file => !docIds.has(file.file_obj.id))
  }

  const trackActivity = async () => {
    try {
      const now = moment()
      const utcMoment = now.utc()
      const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

      let data = {
        data: {
          type: 'gb_action_tracking_logs',
          attributes: {
            affiliate_member_id: getUserId(),
            action: 'sharefile_sync',
            action_time: formattedTime,
            parent_id: customer.id
          }
        }
      }
      await api.post('/Api/V8/module', data)
    } catch (error) {
      console.log(error)
    }
  }

  const handleSyncData = async () => {
    setData({ loading: true, error: '' })
    dispatch(setAllFilesLoader({ loader: true }))
    try {
      const response = await handleSyncDataDocs(customer?.id)

      const { success, files, message } = response?.data
      if (success && files?.length > 0) {
        const prepareDocArr = prepareData(files, customer_files?.docs)
        const prepData = [
          ...(customer_files?.files?.length ? customer_files.files : []),
          ...(Array.isArray(prepareDocArr) ? prepareDocArr : [prepareDocArr])
        ]
        const uniquePrepData = Array.from(
          new Map(
            prepData.map(item => [item.file_obj.document_revision_id, item])
          ).values()
        )

        dispatch(
          setAllCustomerFiles({
            files: uniquePrepData
          })
        )

        const filterAllDocsArr = filterAllDocs(
          uniquePrepData,
          customer_files?.docs
        )
        filterAllDocsArr?.length > 0 &&
          filterAllDocsArr?.map(item =>
            dispatch(
              setSingleDoc({
                file: {
                  fileName: item?.file_name || '',
                  file_obj: {
                    ...item?.file_obj,
                    name: item?.file_obj?.name || item?.file_name
                  },
                  status: 'File uploaded successfully.',
                  progress: 100,
                  isUploaded: true,
                  isError: false
                }
              })
            )
          )
      }
      setData({
        loading: false,
        error: message || 'Error occurred...',
        message: message || ''
      })
      trackActivity()
    } catch (err) {
      const errorMessage = err.response
        ? err.response.data?.message || 'Unknown server error'
        : err.request
          ? 'No response from server'
          : err.message || 'Something went wrong'

      setData({ loading: false, error: errorMessage, message: '' })
    } finally {
      setData({ loading: false })
      dispatch(setAllFilesLoader({ loader: false }))
    }
  }

  const handleFileChange = async e => {
    if (!customer?.id) {
      console.error('Customer ID is required for file upload')
      return
    }

    setPreparingFiles(true)

    dispatch(
      setIdleTime({
        ideal_time_check: true,
        customerID: customer.id || ''
      })
    )

    if (passport?.flag !== true && DragDropTimeLog) {
      DragDropTimeLog('', 'upload_documents')
    }

    const selectedFiles = Array.from(e.target.files)

    const { allowedFiles, nonAllowedFiles } = await filterFiles(selectedFiles)

    setPreparingFiles(false)
    let duplicatesArr = []

    if (allowedFiles?.length > 0) {
      const { validFiles, duplicates, newLocalFiles } = prepareFilesArray(
        allowedFiles,
        currentCustomerFiles
      )
      duplicatesArr = [...duplicates]

      // Set local files first
      setLocalFiles(newLocalFiles)

      if (validFiles?.length > 0) {
        // Reset tracking for new batch
        uploadCountRef.current = validFiles.length
        completedUploadsRef.current = 0
        uploadStartTimeRef.current = null
        uploadEndTimeRef.current = null

        // Set start time before first upload
        uploadStartTimeRef.current = moment()

        for (let index = 0; index < validFiles.length; index++) {
          const fileId = validFiles[index].id
          const file = newLocalFiles.get(fileId)
          if (file) {
            const isLastFile = index === validFiles.length - 1
            await uploadFile({ id: fileId, file, isLastFile })
          }
        }
      }
    }

    if (nonAllowedFiles?.length > 0) {
      setAlertModal({
        open: true,
        message: `The following file(s) are not allowed: ${nonAllowedFiles
          .map(file => file.name)
          .join(', ')}. Please select different files.`
      })
      //setDisallowedImagesModal(true)
      // setDisAllowedFiles(nonAllowedFiles)
    }
    if (duplicatesArr?.length > 0) {
      //setDuplicateImagesModal(true)
      setAlertModal({
        open: true,
        message: `The following file(s) are already uploaded: ${duplicatesArr
          .map(dup => dup.fileName)
          .join(', ')}. Please select different files.`
      })
    }
    e.target.value = null
  }

  const retryUpload = async id => {
    if (!id || retryingIdsRef.current.has(id)) return
    const file = localFiles.get(id)
    if (!file) return

    retryingIdsRef.current.add(id)
    try {
      await uploadFile({ id, file })
    } finally {
      retryingIdsRef.current.delete(id)
    }
  }

  useEffect(() => {
    if (onRetryUploadReady) {
      onRetryUploadReady(() => retryUpload)
    }
  }, [onRetryUploadReady, localFiles])

  const pendingAuthRetryIds = useRef(new Set())

  const uploadFile = async (data, { _isAuthRetry = false } = {}) => {
    const { id, file: passedFile, isLastFile = false, isRetry = false } = data

    const file = passedFile || localFiles.get(id)
    const fileName = file?.name || ''

    try {
      const response = await handleProcessAllFiles(file, id, customer.id)

      pendingAuthRetryIds.current.delete(id)

      if (response?.type === 'doc_failure') {
        setUploadFailed(true)
        setUploadFailedText(response?.message || 'Unknown Error.')
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              status: response?.message || 'Upload failed',
              isUploaded: false,
              isError: true
            }
          })
        )
        // Track completion even on failure (only for batch uploads, not retries)
        if (!isRetry) {
          completedUploadsRef.current += 1
          if (
            isLastFile ||
            completedUploadsRef.current === uploadCountRef.current
          ) {
            uploadEndTimeRef.current = moment()
            await logBatchActivityIfComplete()
          }
        }
        return
      }

      if (response?.length > 0) {
        const fileData = response[0]
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              fileName: fileData?.file_name || '',
              file_obj: fileData,
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false
            }
          })
        )
        dispatch(
          setSingleDoc({
            file: {
              fileName: fileData?.file_name || '',
              file_obj: {
                ...fileData,
                name: fileData?.file_name || ''
              },
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false
            }
          })
        )
      } else {
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              status: 'Upload error.',
              isUploaded: false,
              isError: true
            }
          })
        )
      }

      if (!isRetry) {
        completedUploadsRef.current += 1

        if (
          isLastFile ||
          completedUploadsRef.current === uploadCountRef.current
        ) {
          uploadEndTimeRef.current = moment()
          await logBatchActivityIfComplete()
        }
      }
    } catch (error) {
      const status = error?.response?.status
      const isAuthError = status === 401 || status === 403
      if (isAuthError && !_isAuthRetry) {
        const refreshedToken = localStorage.getItem('access_token')
        if (refreshedToken) {
          pendingAuthRetryIds.current.add(id)
          dispatch(
            setUpdatedDoc({
              id,
              data: {
                status: 'Session refreshed. Retrying upload...',
                progress: 0,
                isUploaded: true,
                isError: false
              }
            })
          )
          await uploadFile({ id, file }, { _isAuthRetry: true })
          return
        }
      }

      let errStatus = ''
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        errStatus = 'Network error. Please check your connection and retry.'
      } else if (error.message?.includes?.('ERR_CONNECTION_REFUSED')) {
        errStatus = 'Server unavailable, Connection refused'
      } else if (isAuthError) {
        errStatus = 'Session expired. Please log in again and retry the upload.'
      } else {
        errStatus = 'Uploading error.'
      }

      dispatch(
        setUpdatedDoc({
          id,
          data: {
            status: errStatus || 'Uploading error',
            isUploaded: false,
            isError: true
          }
        })
      )
      if (!isRetry) {
        completedUploadsRef.current += 1
        if (
          isLastFile ||
          completedUploadsRef.current === uploadCountRef.current
        ) {
          uploadEndTimeRef.current = moment()
          await logBatchActivityIfComplete()
        }
      }
    }
  }

  const logBatchActivityIfComplete = async () => {
    if (
      uploadStartTimeRef.current &&
      uploadEndTimeRef.current &&
      uploadCountRef.current > 0
    ) {
      const agentId = getUserId() || ''
      await logBatchApiActivity({
        contactId: customer.id,
        agentId,
        apiEndpoint: '/Api/V8/custom/customer/process-all-files',
        actionType: 'upload_documents',
        requestPayload: {
          contact_id: customer.id,
          total_files: uploadCountRef.current,
          uploaded_files: completedUploadsRef.current
        },
        startTime: uploadStartTimeRef.current.format('YYYY-MM-DD HH:mm:ss'),
        endTime: uploadEndTimeRef.current.format('YYYY-MM-DD HH:mm:ss')
      })
    }
  }

  const heifImageConverter = async heifImg => {
    let imageFile = heifImg
    try {
      const blob = await heic2any({
        blob: heifImg,
        toType: 'image/jpg'
      })
      imageFile = new File([blob], heifImg.name.replace(/\.[^/.]+$/, '.jpg'), {
        type: 'image/jpg'
      })
      return imageFile
    } catch (error) {
      return
    }
  }

  const filterFiles = async filesData => {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/heif',
      'image/heic',
      'application/pdf'
    ]

    const allowedFiles = []
    const nonAllowedFiles = []

    for (const file of filesData) {
      if (allowedTypes.includes(file.type)) {
        if (file.type === 'image/heif' || file.type === 'image/heic') {
          const convertedFile = await heifImageConverter(file)
          allowedFiles.push(convertedFile)
        } else {
          allowedFiles.push(file)
        }
      } else {
        nonAllowedFiles.push(file)
      }
    }
    return {
      allowedFiles,
      nonAllowedFiles
    }
  }

  const prepareFilesArray = (array = [], currentCustomerFiles = []) => {
    // Normalize full filename so duplicates are checked including extension.
    const normalizeName = n =>
      typeof n === 'string' ? n.trim().toLowerCase() : ''
    const stripExt = n => n.replace(/\.[^.\\/]+$/, '')

    const collectNames = list => {
      const full = new Set()
      const base = new Set()
      for (const f of list || []) {
        const raw =
          f?.file_obj?.name ?? f?.file_obj?.file_name ?? f?.fileName ?? ''
        const norm = normalizeName(raw)
        if (!norm) continue
        full.add(norm)
        const b = stripExt(norm)
        if (b) base.add(b)
      }
      return { full, base }
    }


    const existing = collectNames(currentCustomerFiles)

    const reduxNames = collectNames(customer_files?.files)

    const droppedNamesRaw = (() => {
      try {
        return collectDroppedFileNames(store.getState())
      } catch (_) {
        return new Set()
      }
    })()
    const droppedFull = new Set()
    const droppedBase = new Set()
    droppedNamesRaw.forEach(raw => {
      const norm = normalizeName(raw)
      if (!norm) return
      droppedFull.add(norm)
      const b = stripExt(norm)
      if (b) droppedBase.add(b)
    })

    const allExistingNames = new Set([
      ...existing.full,
      ...reduxNames.full,
      ...droppedFull
    ])
    const allExistingBaseNames = new Set([
      ...existing.base,
      ...reduxNames.base,
      ...droppedBase
    ])

    const seenInBatch = new Set()
    const seenInBatchBase = new Set()

    const duplicates = []
    const validFiles = []
    const newLocalFiles = new Map(localFiles)
    const currentFileCount = currentCustomerFiles.length

    for (const item of array) {
      const name = item?.name ?? ''
      const normalized = normalizeName(name)
      const normalizedBase = stripExt(normalized)
      const id = generateUUID()
      const isDup =
        !normalized ||
        allExistingNames.has(normalized) ||
        seenInBatch.has(normalized) ||
        (!!normalizedBase &&
          (allExistingBaseNames.has(normalizedBase) ||
            seenInBatchBase.has(normalizedBase)))

      const obj = {
        file_no: currentFileCount + validFiles.length,
        id,
        fileName: name,
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: false,
        file_obj: {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: 0
        }
      }

      if (isDup) {
        duplicates.push(obj)
      } else {
        validFiles.push(obj)
        seenInBatch.add(normalized)
        if (normalizedBase) seenInBatchBase.add(normalizedBase)
        newLocalFiles.set(id, item)
      }
    }

    if (duplicates.length) setDuplicateImagesFiles(duplicates)
    if (validFiles.length) setLocalFiles(newLocalFiles)

    for (const file of validFiles) {
      dispatch(setFilterFiles({ file }))
    }

    return { validFiles, duplicates, newLocalFiles }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }
  const checkFilesLoader = customer_files?.filesLoader

  const activeTripType =
    tripAir?.attributes?.air_sea_type || tripSea?.air_sea_type

  const constructBatchOCRPayload = () => {
    const documents = []
    if (passportData?.id && passportData?.files?.length > 0) {
      const passportDocuments = passportData.files
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (passportDocuments.length > 0) {
        documents.push({
          type: 'passport',
          document_id:
            passportDocuments.length === 1
              ? passportDocuments[0]
              : passportDocuments,
          record_id: passportData.id
        })
      }
    }

    const activeTrip = activeTripType === 'air' ? tripAir : tripSea
    const tripId =
      activeTripType === 'air' ? tripAir?.attributes?.id : tripSea?.id

    if (tripId && activeTrip?.files?.cruise?.length > 0) {
      const cruiseDocuments = activeTrip.files.cruise
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (cruiseDocuments.length > 0) {
        documents.push({
          type: 'cruise_id',
          document_id:
            cruiseDocuments.length === 1 ? cruiseDocuments[0] : cruiseDocuments,
          record_id: tripId
        })
      }
    }

    if (
      activeTripType === 'air' &&
      tripId &&
      tripAir?.files?.boarding_pass?.length > 0
    ) {
      const boardingPassDocuments = tripAir.files.boarding_pass
        .filter(
          file =>
            file?.file_obj?.document_revision_id || file?.document_revision_id
        )
        .map(
          file =>
            file.file_obj?.document_revision_id || file?.document_revision_id
        )

      if (boardingPassDocuments.length > 0) {
        documents.push({
          type: 'boarding_pass',
          document_id:
            boardingPassDocuments.length === 1
              ? boardingPassDocuments[0]
              : boardingPassDocuments,
          record_id: tripId
        })
      }
    }

    if (purchase_cards?.length > 0) {
      purchase_cards.forEach(card => {
        const isOcrDone =
          card?.attributes?.ocr_status === '1'
        if (isOcrDone) {
          return
        }

        if (card?.attributes?.id && card?.files?.length > 0) {
          const cardDocuments = card.files
            .filter(
              file =>
                file?.file_obj?.document_revision_id ||
                file?.document_revision_id
            )
            .map(
              file =>
                file.file_obj?.document_revision_id ||
                file?.document_revision_id
            )

          if (cardDocuments.length > 0) {
            documents.push({
              type: 'credit_card',
              document_id:
                cardDocuments.length === 1 ? cardDocuments[0] : cardDocuments,
              record_id: card.attributes.id
            })
          }
        }
      })
    }

    if (purchase_requests?.length > 0) {
      purchase_requests.forEach(pr => {
        if (pr?.files?.pos_receipt?.length > 0) {
          const posDocuments = pr.files.pos_receipt
            .filter(
              file =>
                file?.file_obj?.document_revision_id ||
                file?.document_revision_id
            )
            .map(
              file =>
                file.file_obj?.document_revision_id ||
                file?.document_revision_id
            )

          if (posDocuments.length > 0 && pr?.attributes?.id) {
            documents.push({
              type: 'pos_receipt',
              document_id:
                posDocuments.length === 1 ? posDocuments[0] : posDocuments,
              record_id: pr.attributes.id
            })
          }
        }

        if (pr?.payment_methods?.length > 0) {
          pr.payment_methods.forEach(paymentMethod => {
            if (
              paymentMethod?.files?.slip?.length > 0 &&
              paymentMethod?.attributes?.id
            ) {
              const slipDocuments = paymentMethod.files.slip
                .filter(
                  file =>
                    file?.file_obj?.document_revision_id ||
                    file?.document_revision_id
                )
                .map(
                  file =>
                    file.file_obj?.document_revision_id ||
                    file?.document_revision_id
                )

              if (slipDocuments.length > 0) {
                documents.push({
                  type: 'credit_card_receipt',
                  document_id:
                    slipDocuments.length === 1
                      ? slipDocuments[0]
                      : slipDocuments,
                  record_id: paymentMethod.attributes.id
                })
              }
            }
          })
        }
      })
    }

    return {
      contact_id: id || customerInfo?.id,
      documents: documents
    }
  }

  const hasDocumentsToOCR = useMemo(
    () => constructBatchOCRPayload().documents.length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [passportData, tripAir, tripSea, purchase_cards, purchase_requests, id]
  )

  const purchaseCards = purchase_cards || []

  const hasCardFiles = card =>
    Boolean(
      card?.attributes?.id &&
      Array.isArray(card?.files) &&
      card.files.length > 0
    )

  const { hasCreditCardImage, hasPendingCreditCardOCR } = useMemo(() => {
    let hasImage = false
    let hasPendingOCR = false

    for (const card of purchaseCards) {
      if (!hasCardFiles(card)) continue

      hasImage = true

      if (card?.attributes?.ocr_status !== '1') {
        hasPendingOCR = true
        break
      }
    }

    return {
      hasCreditCardImage: hasImage,
      hasPendingCreditCardOCR: hasPendingOCR,
    }
  }, [purchaseCards])

  const hasMissingAffiliate = useMemo(
    () =>
      (purchase_requests || []).some(
        pr => prHasDocuments(pr) && !pr?.attributes?.affiliate_company_id
      ),
    [purchase_requests]
  )

  const ocrDisabledReason = isCategorizing
    ? 'Categorizing documents...'
    : hasMissingAffiliate
      ? 'Affiliate selection mandatory'
      : !hasDocumentsToOCR
        ? hasCreditCardImage && !hasPendingCreditCardOCR
          ? 'OCR already done.'
          : 'No document available for OCR.'
        : undefined



  const handleOCRClick = async () => {
    if (!hasDocumentsToOCR || isCategorizing || hasMissingAffiliate) return
    dispatch(setLoading(true))
    dispatch(setLoadingMessage('OCR documents processing...'))
    try {
      const payload = constructBatchOCRPayload()
      if (payload.documents.length === 0) {
        console.error(
          'No documents Ids found for batch OCR Multi Thread processing'
        )
        setAlertModal({
          open: true,
          message: 'No documents found for OCR processing'
        })
        return
      }

      const agentId = localStorage.getItem('agent_id') || ''
      const contactId = id || customerInfo?.id

      const response = await logApiActivity({
        contactId,
        agentId,
        apiEndpoint: '/Api/V8/custom/customer/batch-ocr-process-multithreaded',
        actionType: 'batch_ocr',
        requestPayload: payload,
        apiCall: async () => {
          return await handleBatchOCRMultiThread(payload)
        }
      })

      if (response) {
        const now = moment()
        const utcMoment = now.utc()
        const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

        let body = {
          data: {
            type: 'gb_action_tracking_logs',
            attributes: {
              affiliate_member_id: localStorage.getItem('agent_id'),
              action: 'batch_ocr',
              action_time: formattedTime,
              parent_id: contactId
            }
          }
        }
        // api.post('/Api/V8/module', body).catch(error => {
        //   console.error('Error saving batch OCR activity:', error)
        // })
        const cardCheckOverrides = buildCardCheckOverrides(response)
        await checkAndUpdateStatus(cardCheckOverrides)

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.CUSTOMER_DETAILS, id]
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.DOCUMENTS, id]
          }),
        ])
        dispatch(incrementImageRefreshCounter())
        dispatch(setLoading(false))
        dispatch(setLoadingMessage('OCR documents processing completed'))
        dispatch(setTravelModal({ show: true, triggeredByOCR: true }))
      }
    } catch (error) {
      console.error('Batch OCR Multi Thread failed:', error)
    } finally {
      dispatch(setLoading(false))
      dispatch(setLoadingMessage(''))
      dispatch(setCurrentTab(1))
    }
  }

  return (
    <DragAndDropCard height="100%" IsBottomBorder={false} isFUllHeight={true}>
      <Container>
        {/* Upload Documents Section */}
        <SectionContainer>
          <SectionTitle>Upload Documents</SectionTitle>
          <UploadDocumentsContainer onClick={handleUploadClick}>
            <UploadDocumentsRow>
              <UploadDocumentsIconContainer>
                <MdOutlineFileUpload size={25} color="#666" />
              </UploadDocumentsIconContainer>
              <UploadDocumentsText>
                <BoldText>Drop files here</BoldText>
                <RegularText>or click to browse</RegularText>
              </UploadDocumentsText>
              {preparingFiles && (
                <LoadingIndicator>
                  <LoadingSpinner />
                </LoadingIndicator>
              )}
            </UploadDocumentsRow>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </UploadDocumentsContainer>
        </SectionContainer>

        {/* Documents Uploaded Section */}
        <SectionContainer>
          <SectionTitle>Documents Uploaded</SectionTitle>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              '& button': {
                px: 3,
                py: 0.2,
                fontWeight: 700,
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                textTransform: 'none'
              }
            }}
          >
            <Button
              onClick={() =>
                handleDocumentsUploadedStatusChange('documents_uploaded')
              }
              disabled={
                isAnyDocumentUploading ||
                shouldDisableToggleForAgent ||
                isDocumentsUploadedWorking ||
                isDocsUploadedYes
              }
              title={
                isDocsUploadedYes
                  ? 'Documents are already marked as uploaded'
                  : shouldDisableToggleForAgent
                    ? 'Cannot change status: Documents are already marked as uploaded'
                    : isAnyDocumentUploading
                      ? "Cannot mark as 'Yes' while documents are uploading"
                      : isDocumentsUploadedWorking
                        ? 'Documents classification is already running'
                        : 'Mark documents as uploaded'
              }
              sx={{
                flex: 1,
                color: '#000',
                backgroundColor:
                  toggleValue === 'documents_uploaded' ? '#4caf50' : 'white',
                border: '1px solid #e0e0e0',
                '&.Mui-disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  backgroundColor:
                    toggleValue === 'documents_uploaded' ? '#4caf50' : 'white',
                  color: toggleValue === 'documents_uploaded' ? '#000' : '#999'
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() =>
                handleDocumentsUploadedStatusChange('documents_not_uploaded')
              }
              disabled={
                shouldDisableToggleForAgent ||
                isDocumentsUploadedWorking ||
                isDocsUploadedYes
              }
              title={
                shouldDisableToggleForAgent || isDocsUploadedYes
                  ? 'Cannot change status: Documents are already marked as uploaded'
                  : isDocumentsUploadedWorking
                    ? 'Documents classification is already running'
                    : 'Mark documents as not uploaded'
              }
              sx={{
                flex: 1,
                backgroundColor:
                  toggleValue === 'documents_not_uploaded'
                    ? '#f5f5f5'
                    : 'white',
                border: '1px solid #e0e0e0',
                color: '#000',
                '&:hover': {
                  backgroundColor: 'white'
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  backgroundColor: '#f5f5f5',
                  color: '#999'
                }
              }}
            >
              No
            </Button>
          </Box>
          <Divider sx={{ mt: 2, borderColor: '#999', borderWidth: '1px' }} />
        </SectionContainer>

        {/* Manual Classification Complete Section */}
        <SectionContainer>
          <SectionTitle>Manual Classification Complete</SectionTitle>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 1,
              '& button': {
                px: 3,
                py: 0.2,
                fontWeight: 700,
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                textTransform: 'none'
              }
            }}
          >
            <Button
              onClick={() => handleManualClassificationToggle('yes')}
              disabled={!isManualClassAllowed || isManualClassWorking}
              title={
                !isManualClassAllowed
                  ? 'Cannot run manual classification: Doc status is not classification failed'
                  : isManualClassWorking
                    ? 'Manual classification is already running'
                    : 'Run manual classification'
              }
              sx={{
                flex: 1,
                backgroundColor:
                  manualClassToggleValue === 'yes' ? '#4caf50' : 'white',
                color: '#000',
                border: '1px solid #e0e0e0',

                '&.Mui-disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  backgroundColor:
                    manualClassToggleValue === 'yes' ? '#4caf50' : 'white',
                  color: manualClassToggleValue === 'yes' ? '#000' : '#999'
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => handleManualClassificationToggle('no')}
              disabled={!isManualClassAllowed || isManualClassWorking}
              title={
                !isManualClassAllowed
                  ? 'Cannot run manual classification: Doc status is not classification failed'
                  : isManualClassWorking
                    ? 'Manual classification is already running'
                    : 'Run manual classification'
              }
              sx={{
                flex: 1,
                backgroundColor:
                  manualClassToggleValue === 'no' ? '#f5f5f5' : 'white',
                color: '#333',
                border: '1px solid #e0e0e0',

                '&.Mui-disabled': {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  backgroundColor: '#f5f5f5',
                  color: '#999'
                }
              }}
            >
              No
            </Button>
          </Box>
          <Divider sx={{ mt: 2, borderColor: '#999', borderWidth: '1px' }} />
        </SectionContainer>

        {/* Share File Section */}
        <ShareFileSectionContainer>
          <ShareFileBox>
            <ShareFileLogoContainer
              onClick={e => {
                e.stopPropagation()
                if (!checkFilesLoader) handleSyncData()
              }}
            >
              <ShareFileImage src={ShareFileLogo} alt="ShareFile logo" />
            </ShareFileLogoContainer>
            <ShareFileTitle>Share File</ShareFileTitle>
            <LastSyncText>Last Sync: {formattedDate}</LastSyncText>
            <SyncButton
              onClick={e => {
                e.stopPropagation()
                if (!checkFilesLoader) handleSyncData()
              }}
            >
              Click to Sync From Device
            </SyncButton>
          </ShareFileBox>
          {/* OCR Button */}
          <Divider sx={{ my: 2, borderColor: '#999', borderWidth: '1px' }} />
          <OcrButtonContainer>
            <OcrButtonTooltipWrapper title={ocrDisabledReason}>
              <OcrButton
                type="button"
                onClick={handleOCRClick}
                disabled={
                  isBatchOCRMultiThreadDataLoading ||
                  isLoading ||
                  !hasDocumentsToOCR ||
                  isCategorizing ||
                  hasMissingAffiliate
                }
              >
                OCR
              </OcrButton>
            </OcrButtonTooltipWrapper>
          </OcrButtonContainer>
          <Divider sx={{ my: 2, borderColor: '#999', borderWidth: '1px' }} />
        </ShareFileSectionContainer>
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <LoadingContainer>
          <CircularProgress color="inherit" />
          {loadingMessage && <LoadingMessage>{loadingMessage}</LoadingMessage>}
        </LoadingContainer>
      </Backdrop>
    </DragAndDropCard>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px;
  height: 100%;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ShareFileSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  justify-content: space-between;
`

const SectionTitle = styled(Typography)`
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  text-align: left;
`

const UploadDocumentsContainer = styled.div`
  border: 2px dashed #e5e5e5;
  border-radius: 8px;
  padding: 40px 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  min-height: 200px;

  &:hover {
    border-color: #999;
    background-color: #fafafa;
  }
`

const UploadDocumentsRow = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const UploadDocumentsIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f2f2f2;
  margin-bottom: 8px;
`

const UploadDocumentsText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`

const BoldText = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
`

const RegularText = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: #64748b;
`

const ShareFileBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ShareFileTitle = styled(Typography)`
  font-weight: 600;
  font-size: 15px;
  color: #1e293b;
  text-align: center;
`

const ShareFileLogoContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #f2f2f2;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e2e8f0;
  }
`

const ShareFileImage = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`

const LastSyncText = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
  text-align: center;
`

const SyncButton = styled(Button)`
  background-color: white;
  color: #1e293b;
  text-transform: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: white;
    border-color: #e5e5e5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const OcrButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 20px;
`

const OcrButtonTooltipWrapper = styled.span`
  width: 100%;
  max-width: 300px;
`

const OcrButton = styled.button`
  background-color: #000;
  color: #fff;
  text-transform: uppercase;
  padding: 12px 48px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  max-width: 300px;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background-color: #1a1a1a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #666;
  }
`

const LoadingIndicator = styled.div`
  margin-top: 10px;
`

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #000;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const LoadingMessage = styled.div`
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`

export default UploadDocuments
